/**
 * customer-app · 点餐购物车（行数量 / 加减 / 总价 / 生成订单行）
 *
 * 来源：js/cart-logic.js（已迁移）。
 *
 * 职责：基于内存 Map 的会话购物车（与 localStorage 购物车 RestaurantDataLib 独立，
 *   顾客页会话仍以本 Map 为准）。
 *
 * 兼容契约（必须保留，被 ui-controller / cart-service / customer-lite / app 直接 / 间接调用）：
 *   - `window.RestaurantCartLogic`：{ totalQuantity, buildRenderSnapshot, buildOrderPayload,
 *                                     pruneUnknownDishes, createSessionCart }
 *   - `OS.registerModule('cart', …)`
 *   - createSessionCart() 返回的 SessionCart 字段名 / 行为与原 IIFE 完全一致
 *
 * 设计：
 *   - 全部接口 export，给 cart-service.ts / 后续 ui-render.ts / ui-controller.ts 用真实类型
 *   - DishId 用 `string | number` 联合（业务里可能是雪花 ID 字符串，也可能是自增数字）
 *     比较时统一 String() 化，与原实现一致
 *   - Menu / Dish 字段保持开放（`[key: string]: unknown`）—— 业务层会附加 i18n 名称等扩展
 */

/* ─────────── 类型 ─────────── */

export type DishId = string | number;

/** 菜品定义（开放契约） */
export interface Dish {
  id: DishId;
  name?: string;
  price: number;
  category?: string | number | null;
  thermalPrinter?: string | number | null;
  [key: string]: unknown;
}

/** 菜单（开放契约） */
export interface Menu {
  dishes?: Dish[];
  [key: string]: unknown;
}

/** buildRenderSnapshot 输出的单行（含完整 Dish + 数量 + 小计） */
export interface CartRenderRow {
  d: Dish;
  qty: number;
  line: number;
}

export interface CartRenderSnapshot {
  rows: CartRenderRow[];
  sum: number;
  count: number;
}

/** buildOrderPayload 输出的单行（落到下单 / 打印的最终结构） */
export interface CartOrderItem {
  dishId: DishId;
  name: string | DishId;
  price: number;
  quantity: number;
  category: string;
  /** 仅在菜品显式配置了热敏打印机时才有 */
  thermalPrinter?: string;
}

export interface CartOrderPayload {
  items: CartOrderItem[];
  total: number;
}

/** Map 转 record 的中间结构（toQuantityRecord / replaceFromRecord 用） */
export type QuantityRecord = Record<string, number>;

/** createSessionCart 返回值：会话购物车实例 */
export interface SessionCart {
  getMap(): Map<string, number>;
  totalQuantity(): number;
  addOne(dishId: DishId | null | undefined): void;
  /** @param n 正整数份数；非法值（NaN / ≤0）静默忽略 */
  addN(dishId: DishId | null | undefined, n: number | string): void;
  decrement(dishId: DishId | null | undefined): void;
  increment(dishId: DishId | null | undefined): void;
  /** 整行移除：与把数量一键减到 0 等价，但不用循环触发 UI 渲染 */
  remove(dishId: DishId | null | undefined): void;
  clear(): void;
  toQuantityRecord(): QuantityRecord;
  replaceFromRecord(rec: QuantityRecord | null | undefined): void;
  pruneToMenu(menu: Menu | null | undefined): void;
  buildRenderSnapshot(menu: Menu | null | undefined): CartRenderSnapshot;
  buildOrderPayload(menu: Menu | null | undefined): CartOrderPayload;
}

/** RestaurantCartLogic 公共契约 */
export interface RestaurantCartLogic {
  totalQuantity(map: Map<string, number>): number;
  buildRenderSnapshot(map: Map<string, number>, menu: Menu | null | undefined): CartRenderSnapshot;
  buildOrderPayload(map: Map<string, number>, menu: Menu | null | undefined): CartOrderPayload;
  pruneUnknownDishes(map: Map<string, number>, menu: Menu | null | undefined): void;
  createSessionCart(): SessionCart;
}

/* ─────────── 全局 facade 局部断言 ─────────── */

interface RuntimeGlobal {
  RestaurantCartLogic?: RestaurantCartLogic;
  OS?: {
    registerModule?: (name: string, ref: unknown) => unknown;
  };
}

const G = (typeof window !== 'undefined' ? window : globalThis) as unknown as RuntimeGlobal;

/* ─────────── 内部小工具 ─────────── */

/** id 比较：null/undefined 视为不等；其余统一字符串化 */
const dishIdEq = (a: unknown, b: unknown): boolean =>
  a != null && b != null && String(a) === String(b);

/** 将外部 dishId 入参规范成 Map key（trim 后非空才返回） */
const toKey = (dishId: DishId | null | undefined): string => {
  const k = String(dishId ?? '').trim();
  return k;
};

/* ─────────── 顶层纯函数 ─────────── */

const totalQuantity = (map: Map<string, number>): number => {
  let n = 0;
  map.forEach((q) => {
    if (q > 0) n += q;
  });
  return n;
};

const buildRenderSnapshot = (
  map: Map<string, number>,
  menu: Menu | null | undefined,
): CartRenderSnapshot => {
  const dishes: Dish[] = (menu && menu.dishes) || [];
  let sum = 0;
  let count = 0;
  const rows: CartRenderRow[] = [];
  map.forEach((qty, id) => {
    if (qty <= 0) return;
    const d = dishes.find((x) => x && dishIdEq(x.id, id));
    if (!d) return;
    const line = d.price * qty;
    sum += line;
    count += qty;
    rows.push({ d, qty, line });
  });
  return { rows, sum, count };
};

const buildOrderPayload = (
  map: Map<string, number>,
  menu: Menu | null | undefined,
): CartOrderPayload => {
  const dishes: Dish[] = (menu && menu.dishes) || [];
  let total = 0;
  const items: CartOrderItem[] = [];
  map.forEach((quantity, id) => {
    if (quantity <= 0) return;
    const d = dishes.find((x) => x && dishIdEq(x.id, id));
    const price = d ? d.price : 0;
    total += price * quantity;
    const row: CartOrderItem = {
      dishId: id,
      name: d ? d.name ?? id : id,
      price,
      quantity,
      category: d && d.category != null ? String(d.category) : '',
    };
    if (d && d.thermalPrinter != null && String(d.thermalPrinter).trim() !== '') {
      row.thermalPrinter = String(d.thermalPrinter).trim();
    }
    items.push(row);
  });
  return { items, total };
};

const pruneUnknownDishes = (
  map: Map<string, number>,
  menu: Menu | null | undefined,
): void => {
  const dishes: Dish[] = (menu && menu.dishes) || [];
  const ids = new Set<string>();
  for (let di = 0; di < dishes.length; di++) {
    const d = dishes[di];
    if (d && d.id != null) ids.add(String(d.id));
  }
  /** Array.from 拷贝 keys，避免删除时迭代器失效 */
  const keys = Array.from(map.keys());
  for (let i = 0; i < keys.length; i++) {
    const k = keys[i];
    if (!ids.has(String(k))) map.delete(k);
  }
};

/* ─────────── SessionCart 工厂 ─────────── */

const createSessionCart = (): SessionCart => {
  const m = new Map<string, number>();
  return {
    getMap: () => m,
    totalQuantity: () => totalQuantity(m),
    addOne(dishId) {
      const k = toKey(dishId);
      if (!k) return;
      m.set(k, (m.get(k) ?? 0) + 1);
    },
    addN(dishId, n) {
      const k = toKey(dishId);
      if (!k) return;
      const add = parseInt(String(n), 10);
      if (!Number.isFinite(add) || add <= 0) return;
      m.set(k, (m.get(k) ?? 0) + add);
    },
    decrement(dishId) {
      const k = toKey(dishId);
      if (!k) return;
      const next = (m.get(k) ?? 0) - 1;
      if (next <= 0) m.delete(k);
      else m.set(k, next);
    },
    increment(dishId) {
      const k = toKey(dishId);
      if (!k) return;
      m.set(k, (m.get(k) ?? 0) + 1);
    },
    remove(dishId) {
      const k = toKey(dishId);
      if (!k) return;
      m.delete(k);
    },
    clear() {
      m.clear();
    },
    toQuantityRecord() {
      const o: QuantityRecord = {};
      m.forEach((qty, id) => {
        if (qty > 0) o[String(id)] = qty;
      });
      return o;
    },
    replaceFromRecord(rec) {
      m.clear();
      if (!rec || typeof rec !== 'object') return;
      Object.keys(rec).forEach((id) => {
        const q = parseInt(String(rec[id]), 10);
        if (Number.isFinite(q) && q > 0) m.set(String(id), q);
      });
    },
    pruneToMenu(menu) {
      pruneUnknownDishes(m, menu);
    },
    buildRenderSnapshot(menu) {
      return buildRenderSnapshot(m, menu);
    },
    buildOrderPayload(menu) {
      return buildOrderPayload(m, menu);
    },
  };
};

/* ─────────── 暴露 ─────────── */

const lib: RestaurantCartLogic = {
  totalQuantity,
  buildRenderSnapshot,
  buildOrderPayload,
  pruneUnknownDishes,
  createSessionCart,
};

G.RestaurantCartLogic = lib;

if (typeof G.OS?.registerModule === 'function') {
  try {
    G.OS.registerModule('cart', lib);
  } catch {
    /* OS facade 注册失败不致命 */
  }
}
